import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
} from '@mui/material'
import {
  Search,
  CalendarToday,
  LocationOn,
  Group,
  AttachMoney,
  CheckCircle,
  Schedule,
  Warning,
  Cancel,
} from '@mui/icons-material'
import { SidebarLayout } from '@/components/Layout'
import { Booking } from '@/types'
import { BookingsService } from '@/utils/bookings-service'
import { useAuth } from '@/contexts/AuthContext'
import { TripDetailsModal } from '@/components/Bookings/TripDetailsModal'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trips-tabpanel-${index}`}
      aria-labelledby={`trips-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

interface TripCardProps {
  booking: Booking
  onViewDetails: (booking: Booking) => void
}

function TripCard({ booking, onViewDetails }: TripCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success'
      case 'pending': return 'warning'
      case 'cancelled': return 'error'
      case 'completed': return 'info'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <CheckCircle />
      case 'pending': return <Schedule />
      case 'cancelled': return <Cancel />
      case 'completed': return <CheckCircle />
      default: return <Schedule />
    }
  }

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: start.getFullYear() !== end.getFullYear() ? 'numeric' : undefined
    }

    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
  }

  const calculateNights = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  return (
    <Card sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => onViewDetails(booking)}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Property Booking
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Booking ID: {booking.bookingId}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDateRange(booking.startDate, booking.endDate)} • {calculateNights(booking.startDate, booking.endDate)} nights
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Group fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {booking.guests} guests
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                ${booking.totalPrice}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip
              label={booking.status}
              size="small"
              color={getStatusColor(booking.status) as any}
              icon={getStatusIcon(booking.status)}
            />
            {booking.paymentStatus && (
              <Chip
                label={`Payment: ${booking.paymentStatus}`}
                size="small"
                variant="outlined"
                color={booking.paymentStatus === 'paid' ? 'success' : 'warning'}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            size="small" 
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation()
              onViewDetails(booking)
            }}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default function MyTrips() {
  const { userProfile } = useAuth()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Tab and filter state
  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Modal state
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use the new my-trips endpoint to get bookings where user is the guest
      const tripsData = await BookingsService.getMyTrips()
      setBookings(tripsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trips')
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = (booking: Booking) => {
    setSelectedBooking(booking)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedBooking(null)
  }

  // Filter bookings based on current filters
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = searchTerm === '' ||
      booking.bookingId.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter

    return matchesSearch && matchesStatus
  })

  // Group bookings by status for tabs
  const bookingsByStatus = {
    all: filteredBookings,
    upcoming: filteredBookings.filter(b => {
      const startDate = new Date(b.startDate)
      const now = new Date()
      return startDate > now && (b.status === 'confirmed' || b.status === 'pending')
    }),
    current: filteredBookings.filter(b => {
      const startDate = new Date(b.startDate)
      const endDate = new Date(b.endDate)
      const now = new Date()
      return startDate <= now && endDate >= now && b.status === 'confirmed'
    }),
    past: filteredBookings.filter(b => {
      const endDate = new Date(b.endDate)
      const now = new Date()
      return endDate < now || b.status === 'completed'
    }),
    cancelled: filteredBookings.filter(b => b.status === 'cancelled'),
  }

  if (loading) {
    return (
      <SidebarLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          My Trips
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage your travel bookings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search trips..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`All (${bookingsByStatus.all.length})`} />
          <Tab label={`Upcoming (${bookingsByStatus.upcoming.length})`} />
          <Tab label={`Current (${bookingsByStatus.current.length})`} />
          <Tab label={`Past (${bookingsByStatus.past.length})`} />
          <Tab label={`Cancelled (${bookingsByStatus.cancelled.length})`} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {bookingsByStatus.all.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CalendarToday sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No trips found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {bookings.length === 0
                ? 'Start planning your next adventure!'
                : 'Try adjusting your filters to see more results'
              }
            </Typography>
            <Button variant="contained">
              Browse Properties
            </Button>
          </Box>
        ) : (
          bookingsByStatus.all
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
            .map((booking) => (
              <TripCard
                key={booking.bookingId}
                booking={booking}
                onViewDetails={handleViewDetails}
              />
            ))
        )}
      </TabPanel>

      {/* Other tab panels */}
      {[1, 2, 3, 4].map((tabIndex) => {
        const statusKey = ['upcoming', 'current', 'past', 'cancelled'][tabIndex - 1]
        const statusBookings = bookingsByStatus[statusKey as keyof typeof bookingsByStatus]

        return (
          <TabPanel key={tabIndex} value={activeTab} index={tabIndex}>
            {statusBookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No {statusKey} trips
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {statusKey === 'upcoming' && 'Your upcoming trips will appear here'}
                  {statusKey === 'current' && 'Your current trips will appear here'}
                  {statusKey === 'past' && 'Your past trips will appear here'}
                  {statusKey === 'cancelled' && 'Your cancelled trips will appear here'}
                </Typography>
              </Box>
            ) : (
              statusBookings
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                .map((booking) => (
                  <TripCard
                    key={booking.bookingId}
                    booking={booking}
                    onViewDetails={handleViewDetails}
                  />
                ))
            )}
          </TabPanel>
        )
      })}

      {/* Trip Details Modal */}
      <TripDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        booking={selectedBooking}
      />
    </SidebarLayout>
  )
}